import {
  computed,
  isRef,
  reactive,
  ref,
  unref,
  watch,
  type FunctionalComponent,
  type MaybeRef,
  type Ref,
  type VNode
} from 'vue'
import type { FormWizardState, FormWizardStep } from '../types'
import type { TFormModel } from 'components/form/types/formModel'
import { clamp } from '@/utils/math'
import type { IBaseComponentProps } from 'components/base/types'
import type { SelectComponentMap } from 'components/form/form-field/types'

export interface UseFormWizardProps<T extends TFormModel = { [key: string]: any }> extends IBaseComponentProps {
  steps: MaybeRef<FormWizardStep<T>[]>;
  currentIndex?: MaybeRef<number>;
  initialState?: Partial<FormWizardState>;
  noTopPadding?: MaybeRef<boolean>;
  wrapperClass?: MaybeRef<string>;
  outputState?: Ref<T>
  onSave?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>
  onGoBack?: (index: number) => (number | undefined | Promise<number | undefined | void> | void);
  onGoNext?: (index: number) => (number | undefined | Promise<number | undefined | void> | void);
  onClickNext?: () => any;
  onLastGoBack?: (index: number) => void | Promise<void>
  formComponentExtensions?: SelectComponentMap;
  hideGoBack?: MaybeRef<boolean>;
  hideActions?: MaybeRef<boolean>;
  noCloseButton?: boolean
  noTopSection?: boolean
  fullScreen?: boolean
  globalTitle?: string;
  nextButtonText?: MaybeRef<string>;
  nextButtonDisabled?: MaybeRef<boolean>;
  titleBody?: FunctionalComponent<any>;
  onIndexChange?: (index: number) => any
  showFooter?: MaybeRef<boolean>;
  errors?: MaybeRef<string[]>;
  progress?: MaybeRef<number>;
  loadingState?: FunctionalComponent<{ progress: Ref<number> }>;
  loading?: MaybeRef<boolean>;
  banner?: {
    component: FunctionalComponent
    index?: number
  },
  customActionButton?: VNode
}

export type FormWizardHandler<T extends TFormModel = TFormModel> = {
  titles: Ref<string[]>
  state: FormWizardState
  steps: Ref<FormWizardStep<T>[]>
  step: Ref<FormWizardStep<T>>
  handleClose: () => Promise<void>
  handleGoNext: () => Promise<void>
  handleGoBack: () => Promise<void>
  handleCancel: () => Promise<void>
  handleSave: () => Promise<void>
  nextIsDisabled: Ref<boolean>
  props: UseFormWizardProps<T>
  version: Ref<number>;
  setIndex: (index: number) => number;
  __v_skip: true;
}

export const useFormWizard = <T extends TFormModel = TFormModel>(
  props: UseFormWizardProps<T>
): FormWizardHandler<T> => {
  const state = reactive({
    index: 0,
    loading: false,
    goBackLoading: false,
    ...(props.initialState || {})
  })

  const version = ref<number>(0)

  const setIndex = (index: number): number => {
    if (index < 0) return 0;

    const idx = clamp(index, 0, steps.value.length - 1);
    state.index = idx;
    if (props.onIndexChange) {
      props.onIndexChange(idx);
    }
    return idx;
  }

  if (isRef(props.steps)) {
    watch(
      props.steps,
      () => {
        version.value = version.value + 1
      },
      { immediate: true }
    )
  }

  //if (isRef(props.currentIndex)) {
  //  watch(props.currentIndex, (index) => {
  //    setIndex(index);
  //  })
  //}

  const steps = computed(() => {
    return unref(props.steps).map((it) => unref(it))
  })

  const step = computed(() => {
    return unref(steps.value[state.index]) || {}
  })

  const titles = computed((): string[] => {
    return steps.value.map((step) => unref(step).title).filter((it) => !!it) as string[]
  });

  const errors = computed(() => unref(props.errors) || []);

  const nextIsDisabled = computed(() => {
    if (errors.value.length > 0 || unref(props.nextButtonDisabled)) return true;
    const st = step.value
    const formHandler = st.formHandler
    if (!formHandler) return false;

    return formHandler.errors.state.errorCount > 0 || !formHandler.stats.state.touched
  })

  // // @ts-ignore
  // const step = computedWithControl(state, () => {
  //   return steps[state.index]
  // })

  const handleClose = async () => {
    if (props.onCancel) {
      await props.onCancel()
    }
  }

  const handleGoNext = async () => {
    const st = step.value
    const form = st.formHandler

    if (form) {
      const errorCount = await form.errors.refresh({ forceVisible: true })
      if (errorCount > 0) return
    }

    const idx = clamp(state.index + 1, 0, steps.value.length - 1);

    if (props.onGoNext) {
      const next = await props.onGoNext(idx);

      if (typeof next === 'number') {
        setIndex(next);
      } else {
        setIndex(idx);
      }
    } else {
      setIndex(idx);
    }
  }

  const handleGoBack = async () => {
    const unclamped = state.index - 1;
    const idx = setIndex(state.index - 1);

    if (props.onGoBack) {
      state.goBackLoading = true
      const next = await props.onGoBack(idx);

      if (typeof next === 'number') {
        setIndex(next);
      }
    }

    if (unclamped < 0 && props.onLastGoBack) {
      props.onLastGoBack(idx);
    }
    state.goBackLoading = false
  }

  const handleCancel = async () => {
    if (props.onCancel) {
      await props.onCancel()
    }
  }

  const handleSave = async () => {
    if (props.onSave) {
      state.loading = true
      try {
        await props.onSave()
      } catch (e) {
        console.error(e)
      }
      state.loading = false
    }
  }

  return {
    titles,
    state,
    steps,
    step: step as Ref<FormWizardStep<T>>,
    handleClose,
    handleGoNext,
    handleGoBack,
    handleCancel,
    handleSave,
    nextIsDisabled,
    props,
    version,
    setIndex,
    __v_skip: true
  }
}

export type UseFormWizardReturn<T extends TFormModel = TFormModel> = ReturnType<
  typeof useFormWizard<T>
>
